// @ts-check
import classNames from 'classnames';
import { graphql } from 'gatsby';
import React from 'react';
import { useContentStackProps } from '~/contentstack';
import ExpandableContentBlock from '../components/ExpandableContentBlock';
import Overview from '../components/Overview';
import RedBar from '../components/RedBar';
import { withReferencePath } from '../utils/ReferencePaths';
import ColumnBlock from './ColumnBlock';
import CopyBlock, { CopyBlock_REFERENCES } from './CopyBlock';

import './ContentBlock.scss';

export const query = graphql`
	fragment ContentBlockFragment on Ozk_pageContent {
		content_block {
			reference {
				uid
				_version
				heading
				heading_align
				include_red_line
				element_id
				border_options
				restrict_width
				left_image {
					...BynderImageFragment
				}
				include_left_image_on_mobile
				...ExpandableCoverFragment

				modular_blocks {
					...CopyBlockFragment
				}
			}
		}
	}
`;

const [rootPath, withPaths] = withReferencePath('content_block.reference');
export const ContentBlock_REFERENCES = [rootPath, ...withPaths(CopyBlock_REFERENCES)];

/**
 * @typedef {import('./CopyBlock').ModularBlockCopy} Copy
 * @typedef {import('./CopyBlock').ModularBlockCopyContent} CopyContent
 * @typedef { 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 } ColumnsPerRow

 * @typedef ContentBlockModularBlocks
 * @property {string} uid
 * @property {Copy} copy

 * @typedef ContentBlockModel
 * @property {string} uid The unique identifier of this content block.
 * @property {string} heading The main title of this block.
 * @property {import('csstype').Property.TextAlign} heading_align The alignment of the heading.
 * @property {boolean} [include_red_line=false] A value indicating whether there should be a decorative red line above the heading.
 * @property {string} [element_id] The optional ID to attach to the content block element.
 * @property {'top' | 'left' | 'right' | 'bottom' | 'inner'} border_options=[] The list of borders to apply.
 * @property {import('../components/ExpandableContentBlock').ExpandableContentBlockModel[]} [expandable_cover] The optional configuration to make this Content an Expandable section.
 * @property {'none' | 'fill-with-padding' | 'three-fourths'} [restrict_width='none'] The Option to restrict the width a bit.
 * @property {Copy[]} modular_blocks
 * @property {import('../CSTypes').BynderImageOptions[]} left_image
 * @property {boolean} [include_left_image_on_mobile=false]
 *
 * @typedef ContentBlockProps
 * @property {ContentBlockModel} model
 */
/** @type {import('~/types/React').ReactFC<ContentBlockProps>} */
const ContentBlock = ({ model: options }) => {
	const [currentProps] = useContentStackProps({
		staticModel: options,
		contentType: 'content_block',
		contentUid: options?.uid,
	});

	const grouped = GroupCopy(currentProps.modular_blocks);

	const isExpandable = !!currentProps.expandable_cover?.length;

	let contentBlockContent = (
		<Overview
			id={currentProps.element_id}
			className={classNames('content-block', {
				'cs-border-t': currentProps.border_options?.includes('top'),
				'cs-border-l': currentProps.border_options?.includes('left'),
				'cs-border-r': currentProps.border_options?.includes('right'),
				'cs-border-b': currentProps.border_options?.includes('bottom'),
				'cs-border-i': currentProps.border_options?.includes('inner'),
				'heavy-padding': currentProps.restrict_width === 'fill-with-padding',
				'is-expandable': isExpandable,
			})}
			alignment={currentProps.heading_align}
			isInExpandable={isExpandable}
			leftImage={currentProps.left_image?.[0]}
			omitBorderPadding={!currentProps.border_options?.length}
			includeLeftImageOnMobile={currentProps.include_left_image_on_mobile}
		>
			{!!currentProps.include_red_line && <RedBar />}

			{!!currentProps.heading && <h2 className="heading">{currentProps.heading}</h2>}
			<div
				className={classNames('block-container', {
					'col-lg-8': currentProps.restrict_width === 'three-fourths',
				})}
			>
				{grouped.map((val, i) =>
					Array.isArray(val) ? <ColumnBlock options={val} key={i} /> : <CopyBlock options={{ copy: val }} key={i} />
				)}
			</div>
		</Overview>
	);

	if (isExpandable) {
		contentBlockContent = (
			<ExpandableContentBlock id={currentProps.element_id} options={currentProps.expandable_cover[0]}>
				{contentBlockContent}
			</ExpandableContentBlock>
		);
	}

	return contentBlockContent;
};

/**
 * Adjacent Columns are joined into one column block.
 * @param {Copy[]} modularBlocks
 * @returns {(CopyContent | CopyContent[])[]}
 */
function GroupCopy(modularBlocks = []) {
	/** @type {{ ret: (CopyContent | CopyContent[])[]; lastColumns: CopyContent[] }} */
	const { ret, lastColumns } = modularBlocks.reduce(
		(all, curr) => {
			if (curr.copy?.column_option !== 'none') {
				all.lastColumns.push(curr.copy);
			} else {
				// Add the previous columns as a grouped array, if any.
				if (all.lastColumns.length > 0) {
					all.ret.push(all.lastColumns);
					all.lastColumns = [];
				}

				all.ret.push(curr.copy);
			}

			return all;
		},
		{ ret: [], lastColumns: [] }
	);

	// Join the last columns, if they were the last entry.
	if (lastColumns.length > 0) {
		ret.push(lastColumns);
	}

	return ret;
}

export default ContentBlock;
